import { galateaSchema } from '@inkitt/titan/schemas/galatea';
import { galateaNotificationPriorityLiteral, galateaNotificationTypeLiteral } from '@inkitt/types-galatea';
import { createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';
import { c } from '../../contract';

const UserNotifications = createSelectSchema(galateaSchema.userNotifications, {
  metadata: () => z.record(z.string(), z.unknown()),
});
const UserNotificationInsertFull = z.object({
  userId: z.string(),
  key: z.string(),
  title: z.string(),
  body: z.string(),
  isDismissed: z.string().optional(),
  type: z.enum(galateaNotificationTypeLiteral).optional(),
  priority: z.enum(galateaNotificationPriorityLiteral).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  showAfter: z.coerce.date().optional().nullable(),
  showUntil: z.coerce.date().optional().nullable(),
});
const UserNotificationInsert = UserNotificationInsertFull.omit({ userId: true });

export const notificationsCenterRouter = c.router(
  {
    get: {
      summary: 'Get notifications for the user',
      method: 'GET',
      path: '',
      query: z.object({
        offset: z.number().optional(),
      }),
      responses: {
        200: z.object({
          notifications: z.array(UserNotifications),
        }),
      },
    },
    post: {
      summary: 'Create a notification for the user',
      method: 'POST',
      path: '',
      body: UserNotificationInsert,
      responses: {
        201: z.object({
          success: z.boolean(),
        }),
      },
    },
    wrapUpNotificationSetup: {
      summary: 'Setup wrap up notifications for the user',
      method: 'POST',
      path: '/setup/wrap-up-notifications',
      body: z.undefined(),
      responses: {
        201: z.object({
          success: z.boolean(),
        }),
      },
    },
    printBookNotificationSetup: {
      summary: 'Setup print book notifications for the user',
      method: 'POST',
      path: '/setup/print-book-notifications',
      body: z.undefined(),
      responses: {
        201: z.object({
          success: z.boolean(),
        }),
      },
    },
    unreadCount: {
      summary: 'Get the count of unread notifications for the user',
      method: 'GET',
      path: '/unread-count',
      responses: {
        200: z.object({
          count: z.number(),
        }),
      },
    },
    getLastSeen: {
      summary: 'Get the last seen timestamp for the user',
      method: 'GET',
      path: '/last-seen',
      responses: {
        200: z.object({
          lastSeenAt: z.string().optional(),
        }),
      },
    },
    markLastSeen: {
      summary: 'Mark the last seen timestamp for the user',
      method: 'POST',
      path: '/last-seen',
      body: z
        .object({
          timestamp: z.number().optional(),
        })
        .or(z.undefined()),
      responses: {
        201: z.object({
          success: z.boolean(),
        }),
      },
    },
    deleteAll: {
      summary: 'Delete all notifications for the user',
      method: 'DELETE',
      path: '/all',
      body: z.undefined(),
      responses: {
        204: z.object({
          success: z.boolean(),
        }),
      },
    },
    addCommentReactionToNotificationList: {
      summary: 'Add comment reaction to notification list',
      method: 'POST',
      path: '/notification-list-add-comment-reaction',
      body: UserNotificationInsertFull,
      responses: {
        201: z.object({
          success: z.boolean(),
        }),
      },
    },
    sendIterablePushNotification: {
      summary: 'Send Iterable push notification',
      method: 'POST',
      path: '/send-iterable-push-notification',
      body: z.object({
        campaignId: z.number(),
        recipientUserId: z.string().optional(),
        dataFields: z.record(z.string(), z.unknown()).optional(),
      }),
      responses: {
        201: z.object({
          success: z.boolean(),
        }),
      },
    },
    sendIterableJourney: {
      summary: 'Send Iterable Journey',
      method: 'POST',
      path: '/send-iterable-journey',
      body: z.object({
        workflowType: z.string(),
        userId: z.string(),
        dataFields: z.record(z.string(), z.unknown()).optional(),
      }),
      responses: {
        201: z.object({
          success: z.boolean(),
        }),
      },
    },
  },
  { pathPrefix: '/notification-center' },
);
